import { message as antdMessage } from 'antd';
import axios from 'axios';
import get from 'lodash/get';
import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, takeLatest, takeLeading } from 'redux-saga/effects';

import { getRequest, postRequest } from 'utils/redux-saga-requests';

import {
  activateAccount,
  activateAccountFailure,
  activateAccountSuccess,
  editSiteFarmMappings,
  editSiteFarmMappingsFailure,
  editSiteFarmMappingsSuccess,
  loadFarms,
  loadFarmsFailure,
  loadFarmsSuccess
} from './actions';
import ActionTypes from './constants';

export function* requestActivateAccount(
  action: ReturnType<typeof activateAccount>
): SagaIterator {
  const {
    payload: {
      values: { enterpriseId, agriWebbCode, whiteLabel },
      onSuccess,
      onError
    }
  } = action;

  try {
    yield call(
      postRequest,
      `integrations/agriwebb/account/enterprise/${enterpriseId}/activate`,
      {
        agriWebbCode,
        whiteLabel
      }
    );
    yield put(activateAccountSuccess());
    onSuccess?.();
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong. :('
    );
    // Pass the backend message to the caller so the UI can show actionable copy
    // (e.g., "code already used") instead of a generic failure text.
    onError?.(message);
    yield put(activateAccountFailure(message));
  }
}

export function* watchActivateAccountRequest() {
  yield takeLeading(
    ActionTypes.ACTIVATE_ACCOUNT_REQUEST,
    requestActivateAccount
  );
}

export function* requestEditSiteFarmMappings(
  action: ReturnType<typeof editSiteFarmMappings>
): SagaIterator {
  const {
    payload: { values, onSuccess }
  } = action;
  try {
    yield call(
      postRequest,
      `integrations/agriwebb/enterprise/${values.enterpriseId}/site-mapping`,
      [...values.siteMapping]
    );
    yield put(editSiteFarmMappingsSuccess());
    onSuccess?.();
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong. :('
    );
    yield put(editSiteFarmMappingsFailure(message));
  }
}

export function* watchEditSiteFarmMappingRequest() {
  yield takeLatest(
    ActionTypes.EDIT_SITE_FARM_MAPPINGS_REQUEST,
    requestEditSiteFarmMappings
  );
}

export function* requestAgriWebbFarms(
  action: ReturnType<typeof loadFarms>
): SagaIterator {
  const {
    payload: { enterpriseId, onSuccess }
  } = action;
  let agriWebbStatus;
  // 1. Check the enterprise is activated before fetching the farm list
  try {
    const {
      data: { enterpriseIntegration }
    } = yield call(
      getRequest,
      `/enterprise/${enterpriseId}?join=enterpriseIntegration`
    );
    agriWebbStatus = enterpriseIntegration?.agriWebbStatus;
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );
    yield put(loadFarmsFailure(message));
    antdMessage.error('Failed to activate the enterprise.');
  }
  // 2. Fetch farm list and site-farm mappings
  if (agriWebbStatus === 'enabled') {
    try {
      const { data: farms } = yield call(
        getRequest,
        `integrations/agriwebb/enterprise/${enterpriseId}/farm`,
        {},
        false
      );
      const { data: siteMappings } = yield call(
        getRequest,
        `integrations/agriwebb/enterprise/${enterpriseId}/site-mapping`,
        {},
        false
      );
      yield call(onSuccess, farms, siteMappings);
      yield put(loadFarmsSuccess());
    } catch (error) {
      if (!axios.isAxiosError(error)) throw error;
      const message = get(
        error,
        'response.data.message',
        'Sorry, something went wrong.'
      );
      yield put(loadFarmsFailure(message));
      antdMessage.error(
        'Failed to load AgriWebb farms. Please restart the integration from AgriWebb.'
      );
    }
  } else {
    yield put(loadFarmsFailure('Failed to activate the enterprise.'));
  }
}

export function* watchLoadFarmsRequest() {
  yield takeLatest(ActionTypes.LOAD_FARMS_REQUEST, requestAgriWebbFarms);
}

export default function* agriWebbSaga() {
  yield all([
    fork(watchActivateAccountRequest),
    fork(watchEditSiteFarmMappingRequest),
    fork(watchLoadFarmsRequest)
  ]);
}
